import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  Dispatch,
  SetStateAction,
  useState,
  FC,
  PropsWithChildren,
} from 'react';
import { ApolloError } from '@apollo/client';
import { useApolloContext } from 'contexts';
import { useAccessToken } from '@robinpowered/dashboard-apps-common';
import { config } from 'config';
import { Sentry } from 'lib/sentry';
import {
  GetCurrentUserAndOrgsForDashboardShellQuery,
  useGetCurrentUserAndOrgsForDashboardShellQuery,
  useGetCurrentUserDefaultRolesForTenantDashboardShellQuery,
  UserDefaultRoleSlug,
} from 'generated/graphql';
import {
  DEFAULT_PERMISSIONS,
  UseUserPermissions,
  useUserPermissions,
} from 'hooks/useUserPermissions';

type GetCurrentUserAndOrgs = NonNullable<
  GetCurrentUserAndOrgsForDashboardShellQuery['getCurrentUserAndOrgs']
>;
export type Organization = GetCurrentUserAndOrgs['organizations'][number];
export type User = GetCurrentUserAndOrgs['user'];

type AuthContextValue = {
  loading: boolean;
  isLoggedIn: boolean;
  error: undefined | ApolloError;
  hasAuthError: boolean | undefined;
  currentUser: null | User;
  currentOrg: null | Organization;
  setOrgSlug: Dispatch<SetStateAction<string | undefined>>;
  orgSlug?: string;
  refetch?: () => void;
  currentOrgHasVMLicenses: boolean;
  userRoles: UserDefaultRoleSlug[] | undefined;
  userOrganizations: Organization[] | undefined;
  userPermissions: UseUserPermissions;
};

const AuthContext = createContext<AuthContextValue>({
  loading: true,
  isLoggedIn: false,
  error: undefined,
  hasAuthError: false,
  currentUser: null,
  currentOrg: null,
  setOrgSlug: () => {},
  orgSlug: undefined,
  refetch: () => null,
  currentOrgHasVMLicenses: false,
  userRoles: undefined,
  userOrganizations: undefined,
  userPermissions: {
    ...DEFAULT_PERMISSIONS,
    isLoading: false,
  },
});

export const AuthContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { tenantId, setTenantId } = useApolloContext();
  const [orgSlug, setOrgSlug] = useState<string | undefined>();
  const { accessToken } = useAccessToken(config.sessionCookieName);

  const {
    data,
    loading: authLoading,
    error: authError,
    refetch,
  } = useGetCurrentUserAndOrgsForDashboardShellQuery({
    skip: !accessToken,
    fetchPolicy: 'network-only',
  });

  const userOrganizations = useMemo(
    () =>
      data?.getCurrentUserAndOrgs?.organizations.slice().sort((a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }) ?? [],
    [data]
  );

  const currentOrg = useMemo(() => {
    const org =
      data?.getCurrentUserAndOrgs?.organizations.find(
        ({ slug }) => slug === orgSlug
      ) ?? null;

    return org;
  }, [data, orgSlug]);

  // Tell ApolloContext what the current org ID is once we've logged in.
  // ApolloContext will re-create the client with a tenantId header for
  // all future queries.
  useEffect(() => {
    if (currentOrg) {
      setTenantId(currentOrg.id);
      Sentry.setContext('organization', {
        id: currentOrg.id,
        slug: currentOrg.slug,
      });
    }
  }, [currentOrg, setTenantId]);

  useEffect(() => {
    if (data) {
      Sentry.setUser({
        id: data.getCurrentUserAndOrgs?.user?.id,
      });
    }
  }, [data]);

  const hasAuthError =
    authError &&
    // TODO: replace with error link like office app
    [
      'provide authorization',
      'not authenticated',
      'check your credentials',
    ].includes(authError.message.toLowerCase());

  const currentOrgHasVMLicenses =
    currentOrg?.features?.limits.visitorManagementLocations
      .overProvisioningLimit !== 0;

  /**
   * Show the loading state while:
   * 1. The query to get both the current user/orgs is in flight, or:
   * 2. The above query has succeeded but ApolloContext hasn't re-created
   *    a client yet with the tenantId header.
   *
   * Note: We don't wait indefinitely for accessToken. If authLoading is false
   * (query finished or was skipped) and we have no data, we stop loading.
   * This prevents infinite loading when the cookie doesn't exist.
   */
  const loading = authLoading || (!authLoading && !!currentOrg && !tenantId);

  /**
   * User is logged in if:
   * 1. Not loading
   * 2. No auth error
   * 3. Has data (user/orgs successfully fetched)
   *
   * If the query was skipped (no access token), data will be undefined,
   * so isLoggedIn will be false.
   */
  const isLoggedIn = !loading && !hasAuthError && !!data;
  const { data: userRolesData } =
    useGetCurrentUserDefaultRolesForTenantDashboardShellQuery({
      skip: !tenantId,
    });

  const userRoles =
    userRolesData?.getCurrentUserAndOrgs
      ?.userDefaultRoleSlugsForCurrentTenant ?? [];

  const userPermissions = useUserPermissions(
    currentOrg?.id ?? null,
    !!tenantId
  );

  const value: AuthContextValue = {
    loading: loading,
    isLoggedIn,
    error: authError,
    hasAuthError,
    userRoles,
    currentUser: data?.getCurrentUserAndOrgs?.user ?? null,
    userOrganizations,
    currentOrg,
    orgSlug,
    setOrgSlug,
    refetch,
    currentOrgHasVMLicenses,
    userPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  return useContext(AuthContext);
};
